<template>
  <div
    v-if="visible"
    :class="rootClasses"
    v-bind="getPt('root', passThrough, { props, state })"
  >
    <div :class="innerWrapperClasses">
      <span
        :class="iconClasses"
        v-bind="getPt('icon', passThrough, { props, state })"
      >
        <slot v-if="$slots.icon" name="icon" />
        <MeridiaIcon
          v-else
          :name="iconName"
          type="material-symbols-outlined"
          style="font-size: inherit"
        />
      </span>

      <div
        :class="contentClasses"
        v-bind="getPt('content', passThrough, { props, state })"
      >
        <slot v-if="$slots.default" />
        <template v-else>
          <div
            v-if="(props.title || $slots.title) && props.type !== 'inline'"
            :class="titleClasses"
            v-bind="getPt('title', passThrough, { props, state })"
          >
            <slot v-if="$slots.title" name="title" />
            <template v-else>{{ props.title }}</template>
          </div>
          <div
            v-if="props.body || $slots.body"
            :class="bodyClasses"
            v-bind="getPt('body', passThrough, { props, state })"
          >
            <slot v-if="$slots.body" name="body" />
            <template v-else>{{ props.body }}</template>
          </div>
        </template>
      </div>

      <div
        v-if="props.close"
        :class="closeButtonClasses"
        v-bind="getPt('closeButton', passThrough, { props, state })"
      >
        <slot v-if="$slots.close" name="close" :on-close="handleClose" />
        <MeridiaButton
          v-else
          :type="ButtonType.TERTIARY"
          :size="closeButtonSize"
          icon="close"
          @click="handleClose"
          :pt="{
            root: {
              class: '!text-neutral-text-body !p-0',
            },
          }"
        />
      </div>
    </div>

    <div
      v-if="props.type === 'banner'"
      :class="actionsClasses"
      v-bind="getPt('actions', passThrough, { props, state })"
    >
      <MeridiaButton
        :type="ButtonType.TERTIARY"
        :size="ButtonSize.MINI"
        :label="props.secondaryButtonLabel ?? 'Secondary'"
        v-bind="getPt('secondaryButton', passThrough, { props, state })"
        @click="emit('secondaryAction')"
      />
      <MeridiaButton
        :type="ButtonType.PRIMARY"
        :size="ButtonSize.MINI"
        :label="props.primaryButtonLabel ?? 'Primary'"
        v-bind="getPt('primaryButton', passThrough, { props, state })"
        @click="emit('primaryAction')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { MessageProps, MessagePassThroughOptions, MessageState } from './types';
import { PassThrough } from '@primevue/core';
import { usePassThrough } from 'primevue/passthrough';
import { getPt } from '../utils';
import MeridiaIcon from '../icon/MeridiaIcon.vue';
import MeridiaButton from '../button/MeridiaButton.vue';
import { ButtonType, ButtonSize } from '../button/types';

const props = defineProps<MessageProps>();

const emit = defineEmits<{
  close: [];
  primaryAction: [];
  secondaryAction: [];
}>();

defineSlots<{
  default?: () => any;
  icon?: () => any;
  title?: () => any;
  body?: () => any;
  close?: (props: { onClose: () => void }) => any;
}>();

const visible = ref(true);

const state = computed<MessageState>(() => ({
  visible: visible.value,
}));

const handleClose = () => {
  visible.value = false;
  emit('close');
};

const closeButtonSize = computed(() => {
  return props.size === 'big' ? ButtonSize.BASE : ButtonSize.MINI;
});

const innerWrapperClasses = computed(() => {
  switch (props.type) {
    case 'banner':
      return 'flex items-start justify-center w-full';
    case 'inline':
      return 'inline-flex justify-center w-full items-center leading-none';
    default:
      return 'flex items-start justify-center w-full';
  }
});

const iconName = computed(() => {
  switch (props.status) {
    case 'info':
      return 'info';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    case 'success':
      return 'check_circle';
    default:
      return 'info';
  }
});

const rootClasses = computed(() => {
  const baseClasses = ['radius-base border transition-all duration-200'];

  // Type-specific classes
  switch (props.type) {
    case 'banner':
      baseClasses.push('w-full flex flex-col');
      break;
    case 'inline':
      baseClasses.push('inline-flex items-start');
      break;
    case 'toast':
      baseClasses.push('flex items-start shadow-medium');
      break;
  }

  // Size-specific classes
  switch (props.size) {
    case 'big':
      baseClasses.push('p-m gap-m');
      break;
    case 'base':
      baseClasses.push('p-m gap-xs');
      break;
  }

  // Status-specific classes
  switch (props.status) {
    case 'info':
      baseClasses.push('bg-blue-50 border-blue-200');
      break;
    case 'warning':
      baseClasses.push('bg-high-surface-disabled border-high-border-subtle');
      break;
    case 'error':
      baseClasses.push(
        'bg-critical-surface-disabled border-critical-border-subtle'
      );
      break;
    case 'success':
      baseClasses.push('bg-primary-surface-muted border-primary-border-subtle');
      break;
  }

  return baseClasses;
});

const iconClasses = computed(() => {
  const baseClasses = ['flex-shrink-0', 'flex', 'mr-2xs'];

  if (props.title && !props.body) {
    baseClasses.push('self-center');
  } else {
    baseClasses.push('self-start');
  }

  // Size-specific icon sizes
  switch (props.size) {
    case 'big':
      baseClasses.push('text-[20px]');
      break;
    case 'base':
      baseClasses.push('icon-s');
      break;
  }

  // Status-specific colors
  switch (props.status) {
    case 'info':
      baseClasses.push('text-blue-500');
      break;
    case 'warning':
      baseClasses.push('text-high-label-default');
      break;
    case 'error':
      baseClasses.push('text-critical-label-default');
      break;
    case 'success':
      baseClasses.push('text-primary-label-default');
      break;
  }

  return baseClasses;
});

const contentClasses = computed(() => {
  if (props.type === 'inline') {
    return ['flex flex-1 items-center'];
  }
  return ['flex-1 flex flex-col gap-2xs'];
});

const titleClasses = computed(() => {
  const baseClasses = [];

  // Size-specific typography
  switch (props.size) {
    case 'big':
      baseClasses.push('text-title-small');
      break;
    case 'base':
      baseClasses.push('text-body-large');
      break;
  }

  // Status-specific colors
  switch (props.status) {
    case 'info':
      baseClasses.push('text-blue-500');
      break;
    case 'warning':
      baseClasses.push('text-high-label-default');
      break;
    case 'error':
      baseClasses.push('text-critical-label-default');
      break;
    case 'success':
      baseClasses.push('text-primary-label-default');
      break;
  }

  return baseClasses;
});

const bodyClasses = computed(() => {
  const baseClasses = ['text-neutral-text-body whitespace-pre-line'];

  switch (props.size) {
    case 'big':
      baseClasses.push('text-body-large');
      break;
    case 'base':
      baseClasses.push('text-body-base');
      break;
  }

  return baseClasses;
});

const closeButtonClasses = computed(() => {
  return ['flex-shrink-0', 'ml-xs'];
});

const actionsClasses = computed(() => {
  return ['flex items-center justify-end gap-xs mt-s'];
});

const passThrough = usePassThrough(props.pt, {} as MessagePassThroughOptions, {
  mergeSections: false,
  mergeProps: true,
}) as PassThrough<MessagePassThroughOptions>;
</script>
